import { Icon } from '@eventbrite/eds-icon';
import { CalendarChunky, ClockChunky } from '@eventbrite/eds-iconography';
import { gettext } from '@eventbrite/i18n';
import { css, cx } from 'emotion';
import { isEqual } from 'lodash';
import momentTimezone from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import {
    ZOOM_DEFAULT_STARTING_SOON_MINUTES,
    ZOOM_IMG,
} from '../../../constants';
import { isNone, isZoomEmbedAvailable } from '../../../utils';
import { moduleBorderStyle } from '../moduleBorderStyle';
import { ZoomEmbedLazy } from './ZoomEmbedLazy';
import { ZoomJoinInApp } from './ZoomJoin';
import { ZoomLimitations } from './ZoomLimitations';
import { ZoomTimer } from './ZoomTimer';

interface TextField {
    text: string;
}
interface Props {
    data: {
        zoomTopic?: TextField;
        zoomStartDatetime?: TextField;
        zoomDuration?: TextField;
        zoomAgenda?: TextField;
        zoomAssetType?: { text: 'meeting' | 'webinar' };
        zoomAssetRegistrationEnabled?: { value: boolean };
        zoomJoinButtonMinBefore?: TextField;
        zoomJoinUrl?: TextField;
        image?: { url: string };
    };
    event: { locale: string; start: object; id: string };
    userEmail: string;
    userFullName: string;
    zoomSdkChromeOriginTrialsToken?: string;
}

export const ZoomModule: React.FC<Props> = ({
    data: zoomMeeting = {},
    event,
    userEmail,
    userFullName,
    zoomSdkChromeOriginTrialsToken,
}) => {
    const [zoomMeetingStatus, setCurrentStatus] = useState({
        hasStarted: undefined,
        isStarting: undefined,
        hasEnded: undefined,
        isCountingDown: undefined,
    });
    const [isLoadedZoomMeetingStatus, setIsLoadedZoomMeetingStatus] =
        useState(false);
    const zoomTopic = zoomMeeting.zoomTopic?.text || null;
    const startDateTime = zoomMeeting.zoomStartDatetime?.text || null;
    const zoomEventDuration = zoomMeeting.zoomDuration?.text || null;
    const { date, duration } = getZoomFormattedDateTime(
        startDateTime,
        zoomEventDuration,
    );
    const agenda = zoomMeeting.zoomAgenda?.text || '';

    const updateZoomMeetingStatus = useCallback(
        (status) => {
            const wasLoadedZoomMeetingStatus = Object.keys(status).some(
                (o) => status[o] === true,
            );

            if (!wasLoadedZoomMeetingStatus) return;

            const isCurrentStatus = isEqual(zoomMeetingStatus, status);

            if (!isCurrentStatus) {
                setCurrentStatus(status);
                setIsLoadedZoomMeetingStatus(true);
            }
        },
        [zoomMeetingStatus],
    );

    const joinMinBefore =
        zoomMeeting.zoomJoinButtonMinBefore?.text ||
        ZOOM_DEFAULT_STARTING_SOON_MINUTES;
    const url = zoomMeeting.zoomJoinUrl?.text || '';
    const isAvailable = isZoomEmbedAvailable();

    const isZoomWebSDKEnabled = false;

    const isPrimaryButton = !isAvailable;

    const { hasStarted, isStarting, hasEnded, isCountingDown } =
        zoomMeetingStatus;

    const isLive = hasStarted || isStarting;

    useEffect(() => {
        const foreverStatus = {
            hasEnded: false,
            hasStarted: true,
            isCountingDown: false,
            isStarting: false,
        };
        if (isNone(startDateTime)) {
            updateZoomMeetingStatus(foreverStatus);
        }
    }, [updateZoomMeetingStatus, startDateTime]);

    return zoomTopic ? (
        <div
            className={cx(
                moduleBorderStyle,
                'dc-modules__item--zoom',
                'eds-l-pad-all-6',
            )}
        >
            <div className={cx(styles.title, 'eds-l-mar-bot-6')}>
                <span className="eds-l-mar-right-3">
                    <i className="eds-vector-image eds-icon--small">
                        {ZOOM_IMG}
                    </i>
                </span>
                <h2 className="eds-text-bl" data-spec="zoom-topic">
                    {gettext('%(title)s', { title: zoomTopic })}
                </h2>

                {!isNone(startDateTime) ? (
                    <ZoomTimer
                        data={zoomMeeting}
                        event={event}
                        onStatusChange={updateZoomMeetingStatus}
                    />
                ) : null}
            </div>

            <div
                className={cx(
                    styles.main,
                    isLoadedZoomMeetingStatus && styles.active,
                )}
            >
                {zoomMeeting.image?.url && (
                    <div
                        className={cx(
                            styles.title,
                            'eds-l-mar-bot-6',
                            styles.details,
                        )}
                    >
                        <img
                            className={cx(styles.image)}
                            src={zoomMeeting.image.url}
                            alt="zoom module image"
                        />
                    </div>
                )}
                <div>
                    <div
                        className={
                            hasEnded
                                ? ''
                                : cx(styles.details, 'eds-l-pad-bot-6')
                        }
                    >
                        <div className={cx(styles.items)}>
                            <div>
                                <MeetingInfo
                                    id="date"
                                    icon={CalendarChunky}
                                    text={
                                        date ||
                                        gettext('To be defined').toString()
                                    }
                                />
                                <MeetingInfo
                                    id="time"
                                    icon={ClockChunky}
                                    text={
                                        duration ||
                                        gettext('To be defined').toString()
                                    }
                                />
                            </div>
                            <div
                                className={cx(styles.item)}
                                data-testid="organizer"
                            >
                                {!isNone(agenda) && (
                                    <p
                                        className="eds-text-color--ui-800 eds-l-mar-vert-2"
                                        data-spec="zoom-agenda"
                                    >
                                        {agenda}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <footer className={cx(styles.buttonsFooter, 'eds-l-pad-top-4')}>
                    {isZoomWebSDKEnabled && !hasEnded && isAvailable && (
                        <>
                            <ZoomEmbedLazy
                                data={zoomMeeting}
                                disabled={isCountingDown}
                                event={event}
                                joinAppUrl={url}
                                userEmail={userEmail}
                                userName={userFullName}
                                zoomSdkChromeOriginTrialsToken={
                                    zoomSdkChromeOriginTrialsToken
                                }
                            />
                            {gettext(' or ')}
                        </>
                    )}
                    {url && !hasEnded && (
                        <ZoomJoinInApp
                            isPrimaryButton={isPrimaryButton}
                            status={zoomMeetingStatus}
                            url={url}
                            joinMinBefore={joinMinBefore}
                        />
                    )}
                </footer>
                {isZoomWebSDKEnabled && <ZoomLimitations isVisible={isLive} />}
            </div>
        </div>
    ) : null;
};

interface MeetingInfoProps {
    id: string;
    icon: Function;
    text: string;
}
function MeetingInfo({ id, icon: MeetingInfoIcon, text }: MeetingInfoProps) {
    return (
        <div className={cx(styles.item)} data-testid={id}>
            <Icon
                type={<MeetingInfoIcon />}
                size="xsmall"
                color="grey-500"
                title={`${id}-icon`}
            />
            <label
                className={cx(
                    styles.label,
                    'eds-text-weight--heavy',
                    'eds-l-mar-left-2 eds-text-bm',
                )}
            >
                {text}
            </label>
        </div>
    );
}

const styles = {
    title: css`
        display: flex;
        align-items: center;
    `,
    main: css`
        position: relative;
        opacity: 0;
        pointer-events: none;

        .eds-tag {
            position: relative !important;
            right: auto !important;
            bottom: auto !important;
        }
    `,
    active: css`
        opacity: 1;
        pointer-events: auto;
    `,
    buttonsFooter: css`
        display: flex;
        flex-wrap: nowrap;
        justify-content: flex-start;
        align-items: center;
    `,
    image: css`
        max-width: 100%;
        max-height: 100%;
    `,
    details: css`
        border-bottom: 1px solid #eeedf2;
    `,
    items: css`
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 8px;

        @media (max-width: 660px) {
            grid-template-columns: none;
        }
    `,
    item: css`
        display: flex;

        .eds-vector-image {
            position: relative;
            top: 2px;
        }
    `,
    label: css`
        width: calc(100% - 16px - 8px);
    `,
};

//TODO: delete duplicity and move to shared library between different areas (organizer/attendee)
const getLocale = () =>
    (window as any).__SERVER_DATA__?.env?.localeInfo?.locale || 'en_US';

//TODO: delete duplicity and move to shared library between different areas (organizer/attendee)
const getZoomFormattedDateTime = (
    startTime: string | null,
    durationMinutes: string | null,
): { date: string; duration: string } => {
    if (isNone(startTime)) return { date: '', duration: '' };
    const localTimezone = momentTimezone.tz.guess(true);

    const locale = getLocale();
    const momentDateTime = momentTimezone
        .utc(startTime)
        .tz(localTimezone)
        .locale(locale);

    const startingTime = momentDateTime.clone();
    const start = startingTime.format('h:mmA');
    const end = startingTime.add(durationMinutes, 'minutes').format('h:mmA');
    const duration = `${start}-${end}`;

    const date = momentDateTime.format('ddd, MMM D, YYYY');

    return { date, duration };
};
